import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChapterHandlerContextProvider, ChapterHandlerContextType as ChapterHandlerContextType, OpenTaskChangeListener } from ".";
import { ErrorBoundary2 } from "@/pages/ErrorBoundary2";
import { ChapterEntry, SectionId, TaskType } from "@/types/content-types";
import { ScrollTargetId } from "@/hooks/useScroller";
import { useChapter, useSection, useSectionsInChapter, useTasksInSection } from "../appContent/appContentHooks";
import { useToast } from "@/hooks/use-toast";
import { useUserContext } from "../user";
import { config } from "@/config";
import { useConfetti } from "@/contexts/confetti/useConfetti";

type ChapterHandlerProviderProps = {
    children: React.ReactNode;
    chapter: ChapterEntry;
};
export function ChapterHandlerProvider({ children, chapter: chapterProp }: ChapterHandlerProviderProps) {
    const chapterId = chapterProp?.id;
    const currentSectionLocalStorageKey = useMemo(() => `chapter-${chapterId}-section`, [chapterId]);

    const chapter = useChapter({ chapterId });
    const prevChapter = useChapter({ chapterId: chapter?.chapter?.prev_chapter });

    const sections = useSectionsInChapter({ chapterId });
    const orderedSections = useMemo(() => chapter.sectionIds.map(id => sections.get(id)), [chapter.sectionIds, sections]);

    const [openTask, _setOpenTask] = useState<ChapterHandlerContextType["openTask"]>(null);
    const openTaskRef = useRef<typeof openTask>(openTask);

    useEffect(() => {
        openTaskRef.current = openTask;
    }, [openTask]);

    const checkChapterUnlocked = useCallback(() => {
        const isChapterUnlocked =
            config.unlockAll ||
            (
                chapter?.chapter?.has_access &&
                (chapter?.chapter?.number === 1 || prevChapter?.completed)
            );
        return isChapterUnlocked;
    }, [chapter?.chapter?.has_access, chapter?.chapter?.number, prevChapter?.completed]);

    const checkSectionUnlocked = useCallback((sectionId: SectionId) => {
        if (!sections) return false;

        const section = sections.get(sectionId)?.section;

        if (!section) return false;

        const prevSectionCompleted = sections.get(section.prev_section)?.completed ?? true;
        const isSectionUnlocked =
            config.unlockAll ||
            (
                checkChapterUnlocked() &&
                section.has_access &&
                (section?.number === 1 || prevSectionCompleted)
            );

        return isSectionUnlocked;
    }, [checkChapterUnlocked, sections]);

    const [currentSectionId, _setCurrentSectionId] = useState<SectionId>(localStorage.getItem(currentSectionLocalStorageKey) ?? null);
    const setCurrentSectionId = useCallback((sectionId: SectionId, { force = false, skipProgressionCheck = false } = {}) => {
        const unlocked = force || (
            (skipProgressionCheck || checkSectionUnlocked(sectionId)) &&
            sections.get(sectionId)?.section.has_access
        );
        if (!unlocked) return;
        _setCurrentSectionId(sectionId);
        localStorage.setItem(currentSectionLocalStorageKey, sectionId);
    }, [checkSectionUnlocked, currentSectionLocalStorageKey, sections]);

    const taskDialogRef = useRef<HTMLElement>(null);
    const openTaskStack = useRef<ChapterHandlerContextType["openTask"][]>([]);
    const currentSection = useSection({ sectionId: currentSectionId });
    const currentTasks = useTasksInSection({ sectionId: currentSectionId });
    const currentSectionProgress = !currentSection.taskCount ? 0 : currentSection.completedTaskCount / currentSection.taskCount * 100;

    const prevSectionIdRef = useRef<SectionId>(null);
    const currentSectionIdRef = useRef<typeof currentSectionId>(null);

    const openTaskChangeListeners = useRef<Set<OpenTaskChangeListener>>(new Set());

    useEffect(() => {
        prevSectionIdRef.current = currentSectionIdRef.current;
        currentSectionIdRef.current = currentSectionId;
    }, [currentSectionId]);

    useEffect(() => {
        if (!currentSectionIdRef.current) {
            setCurrentSectionId(
                [...sections.values()].find(section => section.section.number === 1)?.id,
                { force: true }
            );
        }
    }, [sections, setCurrentSectionId]);

    const { toast } = useToast();
    const { profile } = useUserContext();
    const userId = profile?.user_id;

    const confettiCtx = useConfetti();
    useEffect(() => {
        if (openTask == null) return;
        confettiCtx?.setTaskOpen(true);
        return () => confettiCtx?.setTaskOpen(false);
    }, [confettiCtx, openTask]);


    const setOpenTask = useCallback((taskType: TaskType, scrollTarget?: ScrollTargetId) => {
        const next = taskType == null ? null : {
            scrollTarget,
            id: currentSection.taskIds?.[taskType],
            type: taskType,
        };

        openTaskChangeListeners.current.forEach(listener => listener?.(next, openTaskRef.current));

        _setOpenTask(next);
        // _setOpenTask((prev) => {
        // Commented out open task stack for now
        // Keeping it just in case we want to add it back in the future
        // if (prev?.type === taskType) return prev;

        // if (!scrollTarget) taskDialogRef.current?.scrollIntoView({ block: "start", behavior: "instant" });

        // if (taskType == null) {
        //     return openTaskStack.current.pop() ?? null;
        // }

        // const next: ChapterHandlerContextType["openTask"] = {
        //     scrollTarget,
        //     id: currentSection.taskIds?.[taskType],
        //     type: taskType,
        // };
        // // console.log(openTaskStack.current, next);
        // openTaskStack.current.push(prev);
        // return next;

        //     return next;
        // });
    }, [currentSection]);

    const value = useMemo((): ChapterHandlerContextType => {

        return {
            currentTasks,
            openTask,
            setOpenTask,

            taskDialogRef,

            orderedSections,
            sections,
            setCurrentSectionId,
            currentSection,
            currentSectionProgress,

            chapter: chapterProp,
            get isChapterUnlocked() {
                return checkChapterUnlocked();
            },
            isSectionUnlocked: checkSectionUnlocked,

            registerOpenTaskChangeListener: (listener) => openTaskChangeListeners.current.add(listener),
            deregisterOpenTaskChangeListener: (listener) => openTaskChangeListeners.current.delete(listener),
        };
    }, [chapterProp, checkChapterUnlocked, checkSectionUnlocked, currentSection, currentSectionProgress, currentTasks, openTask, orderedSections, sections, setCurrentSectionId, setOpenTask]);

    return (
        <ChapterHandlerContextProvider value={value}>
            <ErrorBoundary2 msg="ChapterHandler Inner">
                {children}
            </ErrorBoundary2>
        </ChapterHandlerContextProvider>
    );
}